import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Calendar, Tag, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GLOSSARIO = {
  "jurisprudência": "Conjunto de decisões judiciais repetidas sobre um mesmo assunto, que servem como referência para novos casos.",
  "habeas corpus": "Remédio jurídico que protege a liberdade de ir e vir quando alguém está sofrendo prisão ilegal.",
  "liminar": "Decisão judicial provisória e urgente, tomada antes do julgamento final.",
  "mandado de segurança": "Instrumento para proteger direito líquido e certo contra ato abusivo de autoridade pública.",
  "ADI": "Ação Direta de Inconstitucionalidade — questiona se uma lei fere a Constituição Federal.",
  "STF": "Supremo Tribunal Federal — tribunal máximo do Brasil, responsável por interpretar a Constituição.",
  "STJ": "Superior Tribunal de Justiça — tribunal que uniformiza a aplicação das leis federais no Brasil.",
  "réu": "Pessoa acusada em um processo judicial.",
  "ANPP": "Acordo de Não Persecução Penal — acordo entre o Ministério Público e o investigado para evitar o processo criminal.",
  "voto de qualidade": "Voto de desempate dado pelo presidente de um órgão colegiado quando há empate na votação.",
  "inconstitucional": "Que viola ou contraria a Constituição Federal.",
  "constitucional": "Relativo à Constituição; que está de acordo com a Constituição.",
  "defensoria pública": "Instituição que presta assistência jurídica gratuita aos cidadãos que não podem pagar advogado.",
  "ministério público": "Instituição responsável por fiscalizar o cumprimento das leis e defender os interesses da sociedade.",
  "AGU": "Advocacia-Geral da União — representa judicialmente a União e presta consultoria jurídica ao governo federal.",
  "embargos de declaração": "Recurso para esclarecer pontos obscuros ou omissos de uma decisão judicial.",
  "CDC": "Código de Defesa do Consumidor — lei que protege os direitos dos consumidores.",
  "ECA": "Estatuto da Criança e do Adolescente — lei que estabelece direitos e proteções para crianças e adolescentes."
};

const CATEGORIA_QUERY = {
  "Direito Constitucional": "supreme court brazil",
  "Direito Penal": "police crime law",
  "Direito Civil": "civil law justice",
  "Direito Trabalhista": "labor work employment",
  "Direito Tributário": "tax finance government",
  "Direito Previdenciário": "social security pension",
  "Direito do Consumidor": "consumer rights",
  "Direito Administrativo": "government administration"
};

const STOPWORDS = ['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'para', 'por', 'com', 'que', 'se', 'na', 'no', 'nos', 'nas', 'a', 'o', 'os', 'as', 'um', 'uma'];

const NoticiaDetalhe = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [noticia, setNoticia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltipAtivo, setTooltipAtivo] = useState(null);
  const [unsplashImage, setUnsplashImage] = useState(null);

  useEffect(() => {
    const fetchNoticia = async () => {
      try {
        setLoading(true);
        setError(null);

        const docRef = doc(db, 'noticias', slug);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setError('Notícia não encontrada');
          setLoading(false);
          return;
        }

        const d = docSnap.data();
        const dataVal = d.data;
        const dataStr = dataVal && typeof dataVal.toDate === 'function'
            ? dataVal.toDate().toISOString()
            : dataVal ? (typeof dataVal === 'string' ? dataVal : null) : new Date().toISOString();

        const data = {
          _id: docSnap.id,
          titulo: d.titulo || '',
          subtitulo: d.subtitulo || '',
          link: d.link || '',
          resumo: d.resumo || '',
          categoria: d.categoria || 'Direito',
          fonte: d.fonte || '',
          data: dataStr,
          tags: Array.isArray(d.tags) ? d.tags : [],
          conteudo_completo: d.conteudo_completo || '',
          conteudo_html: d.conteudo_html || `<p>${d.resumo || ''}</p>`,
          imagem_capa: d.imagem_capa || '',
          video_url: d.video_url || '',
        };
        setNoticia(data);

        try {
          const titulo = data.titulo || '';
          const categoria = data.categoria || '';
          const palavrasTitulo = titulo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(' ').filter((p) => p.length > 3 && !STOPWORDS.includes(p)).slice(0, 3).join(' ');
          const categoriaQuery = CATEGORIA_QUERY[categoria] || 'law justice';
          const query = palavrasTitulo ? `${palavrasTitulo} ${categoriaQuery}` : categoriaQuery;
          
          const unsplashRes = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=1&orientation=landscape`, {
            headers: { Authorization: `Client-ID ${process.env.REACT_APP_UNSPLASH_ACCESS_KEY || ''}` },
          });

          if (unsplashRes.ok) {
            const unsplashData = await unsplashRes.json();
            const first = Array.isArray(unsplashData.results) ? unsplashData.results[0] : null;
            const imagemUrl = first?.urls?.regular || null;
            if (imagemUrl) setUnsplashImage(imagemUrl);
          }
        } catch (uErr) { console.error(uErr); }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchNoticia();
  }, [slug]);

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return null;
  };

  const destacarTermosJuridicos = (html) => {
    if (!html) return '';
    let htmlModificado = html;
    const termosOrdenados = Object.keys(GLOSSARIO).sort((a, b) => b.length - a.length);
    termosOrdenados.forEach((termo) => {
      const regex = new RegExp(`(?<!<[^>]*)\\b(${termo})\\b(?![^<]*>)`, 'gi');
      htmlModificado = htmlModificado.replace(regex, (match) => {
        const termoLower = match.toLowerCase();
        const definicao = GLOSSARIO[termoLower] || GLOSSARIO[termo];
        return `<span class="termo-juridico" data-termo="${termoLower}" onclick="window.mostrarTooltip('${termoLower}', '${definicao.replace(/'/g, "\\'")}')">${match}</span>`;
      });
    });
    return htmlModificado;
  };

  useEffect(() => {
    window.mostrarTooltip = (termo, definicao) => {
      setTooltipAtivo({ termo, definicao });
      setTimeout(() => setTooltipAtivo(null), 8000);
    };
    return () => { delete window.mostrarTooltip; };
  }, []);

  if (loading) return <><Header /><div className="flex flex-col items-center justify-center py-20 min-h-screen"><Loader2 className="w-12 h-12 text-[#B49163] animate-spin mb-4" /><p className="text-gray-600 text-lg">Carregando notícia...</p></div><Footer /></>;
  if (error) return <><Header /><div className="max-w-4xl mx-auto px-6 py-16"><Alert variant="destructive"><AlertCircle className="h-5 w-5" /><AlertDescription className="ml-2"><strong>Erro:</strong> {error}</AlertDescription></Alert><button onClick={() => navigate('/noticias')} className="inline-flex items-center gap-2 text-[#B49163] mt-4 bg-transparent border-none cursor-pointer"><ArrowLeft size={20} /> Voltar</button></div><Footer /></>;
  if (!noticia) return null;

  let htmlLimpo = (noticia.conteudo_html || '').replace(/&nbsp;|\u00A0/g, ' ');
  if (!htmlLimpo.includes('<p>')) {
    htmlLimpo = `<p>${htmlLimpo.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>')}</p>`;
  }

  const conteudoComTermos = destacarTermosJuridicos(htmlLimpo);
  const embedUrl = getEmbedUrl(noticia.video_url);

  return (
    <>
      <Header />
      <main className="bg-gray-50 min-h-screen">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <button onClick={() => navigate('/noticias')} className="inline-flex items-center gap-2 text-[#B49163] font-semibold bg-transparent border-none cursor-pointer"><ArrowLeft size={20} /> Voltar para notícias</button>
          </div>
        </div>
        <article lang="pt-BR" className="max-w-4xl mx-auto px-6 py-12">
          {/* Cabeçalho da Notícia: Categoria, Data por Extenso e Fonte */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-1">
              <Tag size={18} className="text-[#B49163]" />
              <span className="text-sm font-semibold text-[#B49163]">{noticia.categoria}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-600 border-l border-gray-300 pl-4">
              <Calendar size={16} />
              <span>
                {noticia.data ? new Date(noticia.data).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
              </span>
            </div>

            {noticia.fonte && (
              <span className="text-sm text-gray-400">Fonte: {noticia.fonte}</span>
            )}
          </div>

          <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#0F172A] mb-4">{noticia.titulo}</h1>

          {noticia.subtitulo && (
            <p className="text-xl md:text-2xl text-slate-600 font-normal leading-snug mb-8 max-w-3xl">
              {noticia.subtitulo}
            </p>
          )}
          
          {/* Exibição das Tags em formato de pílulas escuras */}
          {noticia.tags && noticia.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {noticia.tags.map((tag, index) => (
                <span key={index} className="px-4 py-1.5 bg-[#0F172A] text-white text-sm font-semibold rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          <div className="max-w-4xl mx-auto w-full">
            {(noticia.imagem_capa || unsplashImage) && (
              <div className="mb-8 rounded-lg overflow-hidden shadow-lg w-full">
                <img src={noticia.imagem_capa || unsplashImage} alt={noticia.titulo} className="w-full h-auto object-cover" />
              </div>
            )}
            <div
              className="noticia-corpo w-full text-lg text-slate-800 leading-relaxed text-left mb-12 [&_p]:mb-6"
              dangerouslySetInnerHTML={{ __html: conteudoComTermos }}
            />
          </div>

          {embedUrl && (
            <div className="mb-12 relative w-full rounded-xl overflow-hidden shadow-lg" style={{ paddingTop: '56.25%' }}>
              <iframe src={embedUrl} title="Vídeo" className="absolute inset-0 w-full h-full" allowFullScreen />
            </div>
          )}
        </article>
      </main>
      <Footer />
      {tooltipAtivo && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-[#0F172A] text-white px-6 py-4 rounded-lg shadow-2xl z-50">
          <h4 className="font-bold text-[#B49163] capitalize">{tooltipAtivo.termo}</h4>
          <p className="text-sm">{tooltipAtivo.definicao}</p>
        </div>
      )}
      <style>{`
        .noticia-corpo {
          word-break: normal;
          overflow-wrap: break-word;
        }
        .noticia-corpo p,
        .noticia-corpo li {
          text-indent: 0;
          padding-left: 0;
        }
        .termo-juridico { color: #B49163; font-weight: 600; cursor: pointer; border-bottom: 2px dotted #B49163; }
      `}</style>
    </>
  );
};
export default NoticiaDetalhe;